import React from 'react';
import { createRoot, Root } from 'react-dom/client';
import { post } from '../../util/ApiWrapper';
import * as params from '../../util/getQueryParams';
import { SearchBijlesLoading } from './SearchBijlesLoading';
import { SearchBijlesNotFound, SearchHWBNotFound, SearchBLNotFound } from './SearchBijlesNotFound';
import { SearchBijlesResults } from './SearchBijlesResults';
import { SearchBijlesMap } from './SearchBijlesMaps';
import { SearchBijlesControls } from './SearchBijlesControls';
import SubmitEvent = JQuery.SubmitEvent;
import { SearchBijlesError, SearchBijlesResponse, SearchBijlesResult } from './SearchBijles.model';

let queryParams = params.getQueryParams(document.location.search);

type Filters = SearchBijlesResponse['filters'];
type Results = SearchBijlesResult[];

/**
 * Search results page for Bijles, Huiswerkbegeleiding basisschoolbegeleiding.
 *
 * @template ext, bl, hwb
 * @template landingspage-stad-bl
 * @template landingspage-stad-hwb
 *
 * On the stadslandingspagina template the search param is set in the html.
 * <div data-module='searchBijles' data-post='{"City": "Leiden"}'></div>
 */
export class SearchBijles {
  private resultsRoot: Root | null = null;
  private controlsRoot: Root | null = null;
  private mapsRoot: Root | null = null;

  /**
     * The products as specified in the template.
     *
     * Options:
     * - huiswerkbegeleiding
     * - bijles
     * - basisschoolbegeleiding
     * - hub
     */
  public product: string;

  public list_type: string;

  /**
     * SearchParams is set in the constructor so we can test it
     * @param searchParams
     */
  constructor(searchParams = queryParams) {
    // Initialize roots
    this.initializeRoots();

    const htmlCity = $('[data-module="searchBijles"]').data('post') || {};
    const $form = $('.front-search__form');
    this.product = $('[data-container="search-bijles-results"]').data('product');
    this.list_type = $('[data-container="search-bijles-results"]').data('list_type');

    if (!searchParams.city && htmlCity.city) {
      // if there is no city in the url check data-post attr
      this.fetchResults(htmlCity.city);
    } else if (searchParams.city) {
      // get city from users address bar
      this.fetchResults(searchParams.city as string);
    } else {
      // get all results
      this.fetchResults();
    }

    // bind events
    $form.on('submit', (e: SubmitEvent) => this.searchBijles(e));
  }

  private initializeRoots(): void {
    const resultsContainer = $('[data-container="search-bijles-results"]')[0];
    const controlsContainer = $('[data-container="seach-bijles-controls"]')[0];
    const mapsContainer = $('[data-container="seach-bijles-maps"]')[0];

    if (resultsContainer) {
      this.resultsRoot = createRoot(resultsContainer);
    }
    if (controlsContainer) {
      this.controlsRoot = createRoot(controlsContainer);
    }
    if (mapsContainer) {
      this.mapsRoot = createRoot(mapsContainer);
    }
  }

  /**
     * fetches results without reloading the page
     * gets called when user searches from the Bijles Search page
     */
  public searchBijles(e: SubmitEvent): void {
    e.preventDefault();

    const searchInput = document.querySelector<HTMLInputElement>('input.js-search-bijles');

    if (!searchInput) {
      console.error('Search input element not found');
      return;
    }
    const searchTerm = searchInput.value.trim();
    if (!searchTerm) {
      // You could trigger a UI notification here
      console.warn('Please enter a search term');
      return;
    }
    this.renderLoading();
    this.setWindowLocationSearchTerm(searchTerm);
    this.fetchResults(searchTerm);
  }

  /**
     * Fetches results for the provided city. If no city has been provided
     * all results are returned.
     */
  public async fetchResults(city: string = null): Promise<void> {
    const products = ['huiswerkbegeleiding', 'bijles', 'basisschoolbegeleiding', 'hub'];
    const method = products.includes(this.product) ? this.product : 'all';

    try {
      const data: SearchBijlesResponse = await post({
        method: `get${method}Locations`,
        controller: 'TrainingSearchResults',
        data: {
          city: city,
        },
      });

      this.fetchSuccess(data);
    } catch (e) {
      this.fetchFail(city);
    }
  }

  /**
     * Either call the results or the error callback
     * @param data
     */
  public fetchSuccess(data: SearchBijlesResponse): void {
    let value: string;

    if (data && data.results.length > 0
            && Array.isArray(data.results)) {
      value = data.results[0].Distance;
      this.renderResults(data.results, data.filters);

    } else {
      value = 'null';
      this.renderError(data.results as string);
    }

    // Send a custom event to Google analytics if the query param is set.
    if (queryParams.city) {
      (window as any).dataLayer.push({
        event: 'eventTracking',
        category: 'srp_afstand',
        action: 'vertonen',
        label: value,
      });
    }
  }

  /**
     * Call the render error method
     * @param city
     */
  public fetchFail(city: string): void {
    this.renderError(`De zoekterm ${city} heeft geen resultaten opgeleverd.`);
  }

  /**
     * Render the list of results
     * @param results (array) - list of search results
     * @param filters (object) - search filters
     */
  public renderResults(results: Results, filters: Filters): void {
    $('.google-map--resizable').show();

    this.resultsRoot?.render(
      <SearchBijlesResults results={results} filters={filters} product={this.product} list_type={this.list_type} />
    );

    this.controlsRoot?.render(
      <SearchBijlesControls />
    );

    this.mapsRoot?.render(
      <SearchBijlesMap results={results} filters={filters} product={this.product} />
    );
  }

  /**
     * Renders error
     * in case no bijlessen are found
     * in case no HWB are found
     * in case no Basischool begeleiding are found
     * random
     * @param error
     */
  public renderError(error: SearchBijlesError): void {
    $('.google-map--resizable').hide();
    const element = document?.querySelector('.typeBegeleiding');
    if (!element) {
      return;
    }
    const dataAttribute = element.getAttribute('data-product');

    let ErrorComponent;
    switch (dataAttribute) {
      case 'bijles':
        ErrorComponent = <SearchBijlesNotFound error={error} />;
        break;
      case 'basisschoolbegeleiding':
        ErrorComponent = <SearchBLNotFound error={error} />;
        break;
      case 'huiswerkbegeleiding':
      default:
        ErrorComponent = <SearchHWBNotFound error={error} />;
        break;
    }

    this.resultsRoot?.render(ErrorComponent);
  }

  /**
     * Shows gray boxes while fetching the results from the backend
     */
  public renderLoading(): void {
    this.resultsRoot?.render(<SearchBijlesLoading />);
  }

  /**
     * Update users address bar with the right search term
     * @param searchTerm (string) - searched city or zipcode
     */
  public setWindowLocationSearchTerm(searchTerm: string): void {
    let newurl = window.location.origin + window.location.pathname + '?city=' + searchTerm;
    window.history.pushState({ path: newurl }, '', newurl);
  }

  public destroy(): void {
    this.resultsRoot?.unmount();
    this.controlsRoot?.unmount();
    this.mapsRoot?.unmount();
  }
}
